import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Languages, Moon, Sun, LayoutDashboard, Layers3 } from "lucide-react";
import { useLang, BLOG_INFO } from "../context/LangContext";
import { useVisibleBlogs } from "../lib/visibleBlogs";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

export default function Navbar() {
  const visibleBlogs = useVisibleBlogs();
  const { lang, toggle, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("rmb_theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-background/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-xl">
            <img src="/logo.png" alt="ReiMultiBlog" className="h-9 w-9 object-cover" />
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight">
            Rei<span className="text-primary">MultiBlog</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {(visibleBlogs || []).map((b) => (
            <Link
              key={b}
              to={`/blog/${b}`}
              data-testid={`nav-blog-${b}`}
              className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
              style={{ ["--dot"]: BLOG_INFO[b].color }}
            >
              <span className="inline-block h-2 w-2 rounded-full mr-2 align-middle" style={{ backgroundColor: BLOG_INFO[b].color }} />
              {lang === "pt" ? BLOG_INFO[b].pt : BLOG_INFO[b].en}
            </Link>
          ))}
          <Link
            to="/sobre"
            data-testid="nav-about"
            className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
          >
            {t("Sobre", "About")}
          </Link>
          <Link
            to="/sugestoes"
            data-testid="nav-suggestions"
            className="px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
          >
            {t("Sugestões", "Suggestions")}
          </Link>
          <Link
            to="/contribuir"
            data-testid="nav-contribute"
            className="px-3 py-2 rounded-lg text-sm font-medium text-primary hover:text-foreground hover:bg-white/5 transition-colors"
          >
            {t("Contribuir", "Contribute")}
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle-button"
            onClick={toggle}
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            title="PT / EN"
          >
            <Languages className="h-4 w-4" />
            {lang.toUpperCase()}
          </button>
          <button
            data-testid="theme-toggle-button"
            onClick={() => setDark((d) => !d)}
            className="rounded-lg p-2 text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            title="Theme"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Button
            data-testid="nav-admin-btn"
            onClick={() => navigate(user ? "/admin" : "/admin/login")}
            className="gap-2"
          >
            <LayoutDashboard className="h-4 w-4" />
            {user ? t("Painel", "Dashboard") : t("Admin", "Admin")}
          </Button>
        </div>
      </div>
    </header>
  );
}
